import { useEffect, useRef, useState } from 'react';
import { fetchVideoDetails } from '@api/fetch-video-details';
import { IReference, TNullable } from '@common/types';
import { Button } from '@components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog';
import { cn } from '@lib/utils';
import { Loader2, Maximize2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface IVideoReferenceDialogProps {
  reference: TNullable<IReference>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function VideoReferenceDialog(props: IVideoReferenceDialogProps) {
  const { t } = useTranslation();
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoUrl, setVideoUrl] = useState<TNullable<string>>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);

  useEffect(() => {
    if (!props.open || !props.reference?.sourceDocumentId) {
      setVideoUrl(null);
      setHasError(false);
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    setIsLoading(true);
    setHasError(false);
    setVideoUrl(null);

    fetchVideoDetails(props.reference.sourceDocumentId)
      .then((response) => {
        if (!isMounted) return;
        if (response?.videoUrl) {
          setVideoUrl(response.videoUrl);
          return;
        }
        setHasError(true);
      })
      .catch((error: unknown) => {
        console.error(error);
        if (isMounted) {
          setHasError(true);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [props.open, props.reference?.sourceDocumentId]);

  async function handleFullscreen() {
    const videoElement = videoRef.current as
      | (HTMLVideoElement & { webkitEnterFullscreen?: () => void })
      | null;

    try {
      if (videoContainerRef.current?.requestFullscreen) {
        await videoContainerRef.current.requestFullscreen();
      } else if (videoElement?.webkitEnterFullscreen) {
        videoElement.webkitEnterFullscreen();
      }
    } catch (error: unknown) {
      console.error(error);
    }
  }

  const title = props.reference?.name
    ? `${t('common.video')}: ${props.reference.name}`
    : t('common.video');

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-5xl gap-3 rounded-md p-4 sm:p-6">
        <DialogHeader className="pr-8">
          <DialogTitle className="text-left text-base leading-6 sm:text-lg">{title}</DialogTitle>
          <DialogDescription className="sr-only">{title}</DialogDescription>
        </DialogHeader>
        <div
          ref={videoContainerRef}
          className={cn(
            'relative flex aspect-video max-h-[72vh] min-h-48 w-full items-center justify-center overflow-hidden rounded-md bg-black',
            'fullscreen:h-screen fullscreen:max-h-none fullscreen:w-screen fullscreen:rounded-none'
          )}
        >
          {isLoading && (
            <div className="flex flex-col items-center gap-2 text-sm text-white">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>{t('video.loading')}</span>
            </div>
          )}
          {hasError && !isLoading && (
            <p className="px-4 text-center text-sm text-white">{t('video.unavailable')}</p>
          )}
          {videoUrl && !isLoading && !hasError && (
            <video
              ref={videoRef}
              className="h-full w-full bg-black"
              src={videoUrl}
              controls
              playsInline
            />
          )}
        </div>
        <div className="flex justify-end">
          <Button
            className="gap-2"
            type="button"
            variant="outline"
            onClick={handleFullscreen}
            disabled={!videoUrl}
          >
            <Maximize2 className="h-4 w-4" />
            {t('video.fullscreen')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default VideoReferenceDialog;
